import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import _isEmpty from 'lodash/isEmpty';
import Isvg from 'react-inlinesvg';
import { intlShape, intlMessage } from 'constants/propTypes';
import { PFE_ICONS } from 'constants/icons';
import { classNameWithModifiers as withMods } from 'helpers/class-name';
import { Card } from 'components/Card';
import Message from 'components/Message';
import {
  sortRosterByAlpha,
  sortRosterByVisits,
  sortRosterByRecent,
  sortRosterByRatingHighToLow,
} from './Roster.helpers';

import { SvgPrint } from '../Svgs';
import { Dropdown } from '../Dropdown';
import SortMenu from './SortMenu';
import './Roster.scss';

export const messages = defineMessages({
  ariaLabelPrintRoster: {
    id: 'roster.sort-menu.aria-label-print-roster',
    defaultMessage: 'Print roster',
  },
  ariaLabelSortRoster: {
    id: 'roster.sort-menu.aria-label-sort-roster',
    defaultMessage: 'Sort roster',
  },
  sortMenuTitle: {
    id: 'roster.sort-menu.title',
    defaultMessage: 'Sort by',
  },
  alphabetic: {
    id: 'roster.sort-menu-item.alphabetic',
    defaultMessage: 'A to Z',
  },
  visits: {
    id: 'roster.sort-menu-item.visits',
    defaultMessage: 'Most Visits',
  },
  recent: {
    id: 'roster.sort-menu-item.recent',
    defaultMessage: 'Most Recent',
  },
  ratingHighToLow: {
    id: 'roster.sort-menu-item.ratingHighToLow',
    defaultMessage: 'Rating High To Low',
  },
});

export const SORT_OPTION = {
  ALPHABETIC: {
    key: 'ALPHABETIC',
    sortName: messages.alphabetic,
    sortMethod: sortRosterByAlpha,
  },
  VISITS: {
    key: 'VISITS',
    sortName: messages.visits,
    sortMethod: sortRosterByVisits,
  },
  RECENT: {
    key: 'RECENT',
    sortName: messages.recent,
    sortMethod: sortRosterByRecent,
  },
  RATING_HIGH_TO_LOW: {
    key: 'RATING_HIGH_TO_LOW',
    sortName: messages.ratingHighToLow,
    sortMethod: sortRosterByRatingHighToLow,
  },
};

export class Roster extends Component {
  static propTypes = {
    RowComponent: PropTypes.elementType.isRequired,
    getKey: PropTypes.func.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    EmptyComponent: PropTypes.func,
    name: intlMessage,
    printHref: PropTypes.string,
    sort: PropTypes.string,
    sortableField: PropTypes.string,
    sortOptions: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        sortMethod: PropTypes.func,
      })
    ),
    noScroll: PropTypes.bool,
    className: PropTypes.string,
    intl: intlShape,
  };

  static defaultProps = {
    data: [],
  };

  state = {
    sort: SORT_OPTION.ALPHABETIC.key,
  };

  handleSortSelect = option => {
    this.setState({
      sort: option.key,
    });
  };

  render() {
    const { sort: sortState } = this.state;
    const {
      data,
      getKey,
      name,
      printHref,
      RowComponent,
      EmptyComponent,
      sort: sortProp,
      sortOptions,
      noScroll,
      intl,
      className,
      sortableField,
      ...restProps
    } = this.props;
    const sort = sortProp ?? sortState;
    const { sortMethod } = SORT_OPTION[sort];
    const isSortable = !!sortOptions;

    return (
      <Card className={withMods('roster', { noScroll }, className)} flush>
        <div className="roster__container">
          {(name || isSortable || printHref) && (
            <header className="roster__header">
              <Message as="h6" className="roster__header-title" message={name} />
              {isSortable && (
                <Dropdown
                  Toggle={({ isOpen, toggleMenu }) => (
                    <button
                      type="button"
                      aria-label={intl.formatMessage(messages.ariaLabelSortRoster)}
                      aria-haspopup
                      aria-expanded={isOpen}
                      aria-controls="roster-list-item__sort-menu"
                      onClick={toggleMenu}
                      className="roster-list-item__menu-icon">
                      <Isvg className="roster__filter-icon" src={PFE_ICONS.filter} />
                    </button>
                  )}
                  Content={props => (
                    <SortMenu
                      title={intl.formatMessage(messages.sortMenuTitle)}
                      onSelect={this.handleSortSelect}
                      options={sortOptions}
                      sort={sort}
                      id="roster-list-item__sort-menu"
                      {...props}
                    />
                  )}
                />
              )}
              {printHref && (
                <a
                  aria-label={intl.formatMessage(messages.ariaLabelPrintRoster)}
                  className="roster__print-link"
                  href={printHref}
                  target="_blank"
                  rel="noopener noreferrer">
                  <SvgPrint className="roster__print-icon" />
                </a>
              )}
            </header>
          )}

          {_isEmpty(data) && <EmptyComponent {...restProps} />}

          {RowComponent && (
            <ul className="roster__list">
              {sortMethod(data, sortableField).map(item => (
                <RowComponent key={getKey(item)} {...restProps} {...item} />
              ))}
            </ul>
          )}
        </div>
      </Card>
    );
  }
}

export default injectIntl(Roster);
